import { Injectable, NgZone, OnDestroy, inject } from '@angular/core';
import {
  HubConnection,
  HubConnectionBuilder,
  HubConnectionState,
  IHttpConnectionOptions,
} from '@microsoft/signalr';
import {
  BehaviorSubject,
  Subject,
  filter,
  firstValueFrom,
  map,
  take,
} from 'rxjs';
import { environment } from '../environment';
import { AuthCookieService } from './auth-cookie.service';
import { AuthService } from './auth.service';

@Injectable({ providedIn: 'root' })
export class SignalRService implements OnDestroy {
  private connection!: HubConnection;

  // Stänger anslutningen aktivt när sidan lämnas så att servern får en direkt
  // disconnect istället för att vänta ut ClientTimeoutInterval.
  private readonly pageHideHandler = () => this.stop();

  private logoutSubject = new Subject<void>();
  public logout$ = this.logoutSubject.asObservable();

  private projektTaken = new Subject<string>();
  projektTaken$ = this.projektTaken.asObservable();

  private lockResolver = (_value?: unknown) => {
    /* empty */
  };

  reconnectRetryDelay = [0, 2000, 5000, 10_000];
  reconnectIndex = 0;

  connected$ = new BehaviorSubject<boolean>(false);

  currentProjectId: string | undefined;

  private authService = inject(AuthService);
  private authCookieService = inject(AuthCookieService);
  private zone = inject(NgZone);
  private env = inject(environment);
  private alreadyLoggedInSource = new BehaviorSubject<{
    operation: string | undefined;
  }>({
    operation: undefined,
  });
  alreadyLoggedIn$ = this.alreadyLoggedInSource.asObservable();

  init(scopes: string[], authority: string) {
    const options = {
      accessTokenFactory: async () => {
        return firstValueFrom(
          this.authService
            .getAccessToken(scopes, authority)
            .pipe(map((a) => a.accessToken)),
        );
      },
      headers: {
        'Ocp-Apim-Subscription-Key': this.env.apimSubscriptionKey,
      },
    } as IHttpConnectionOptions;

    this.connection = new HubConnectionBuilder()
      .withUrl(`${this.env.etjansterCommonApiUrl}/hub`, options)
      .withAutomaticReconnect({
        nextRetryDelayInMilliseconds: () => this.getRetryDelay(),
      })
      .build();

    this.setEventListeners();
    this.startConnection();
    this.connection.onreconnected(() => {
      this.onReconnect();
    });
    this.connection.onclose(() => {
      this.lockResolver();
    });

    globalThis.removeEventListener('pagehide', this.pageHideHandler);
    globalThis.addEventListener('pagehide', this.pageHideHandler);
  }

  // Best-effort graceful shutdown. Anropas vid avsiktlig navigering/unload –
  // aldrig vid tillfälliga nätverksavbrott (de sköts av automatisk återanslutning).
  stop() {
    if (
      this.connection?.state === HubConnectionState.Connected ||
      this.connection?.state === HubConnectionState.Connecting
    ) {
      this.connection.stop().catch((err) => {
        console.error('Failed to stop SignalR connection gracefully', err);
      });
    }
  }

  ngOnDestroy() {
    globalThis.removeEventListener('pagehide', this.pageHideHandler);
    this.stop();
  }

  signalRLoaded() {
    this.reconnectIndex = 0;
    this.connection.invoke(
      'registerConnection',
      this.authCookieService.getSessionId(),
    );
    this.connected$.next(true);

    const promise = new Promise((res) => {
      this.lockResolver = res;
    });
    navigator.locks.request('signalr_session_lock', { mode: 'shared' }, () => {
      return promise;
    });
  }

  invoke<T>(method: string, data?: T) {
    if (data) {
      this.connection.invoke(method, data);
    } else {
      this.connection.invoke(method);
    }
  }

  logoutUser(reason: string) {
    this.logoutSubject.next();
    this.zone.runOutsideAngular(() => {
      setTimeout(() => {
        this.authService
          .logout(
            `${this.env.etjansterCommonUrl}/public/logout?reason=${reason}`,
          )
          .pipe(take(1))
          .subscribe();
      }, 0);
    });
  }

  registerSharedProject(projectId: string) {
    this.currentProjectId = projectId;
    this.connected$
      .pipe(
        filter((connected) => connected),
        take(1),
      )
      .subscribe(() => {
        this.connection.invoke('registerProject', this.currentProjectId);
      });
  }

  leaveSharedProject() {
    this.invoke('LeaveProject');
    this.currentProjectId = undefined;
  }

  private startConnection() {
    this.connection
      .start()
      .then(() => this.signalRLoaded())
      .catch(() => {
        this.zone.runOutsideAngular(() => {
          setTimeout(() => this.startConnection(), this.getRetryDelay());
        });
      });
  }

  private setEventListeners() {
    this.connection.on('logOutUsers', () => {
      this.logoutUser('maintenance');
    });

    this.connection.on('alreadyLoggedIn', () => {
      this.alreadyLoggedInSource.next({ operation: 'open' });
    });

    this.connection.on('notifyUserLoginTakeover', () => {
      this.logoutUser('takeover');
    });

    this.connection.on('alreadyLoggedInSameBrowser', () => {
      this.alreadyLoggedInSource.next({ operation: 'openSameBrowser' });
    });

    this.connection.on('sharedProjectTaken', (sharedProjectId) => {
      this.projektTaken.next(sharedProjectId);
    });
  }

  private onReconnect() {
    this.reconnectIndex = 0;
    this.signalRLoaded();
    if (this.currentProjectId) {
      this.registerSharedProject(this.currentProjectId);
    }
  }

  private getRetryDelay() {
    const retryIndex = Math.min(
      this.reconnectIndex,
      this.reconnectRetryDelay.length - 1,
    );
    this.reconnectIndex++;
    return this.reconnectRetryDelay[retryIndex];
  }
}
